import { Fragment, useSyncExternalStore } from 'react';
import { Plus, Calendar as CalendarIcon } from '../../icons';
import { TYPE_COLOR_MAP, type CalendarDto, type CalendarEventDto, type CalendarEventType } from '../../api/calendar';
import { tCalendar, getCalendarLang, subscribeCalendarLang } from '../../lib/calendar-i18n';
import { ReservationBadge } from './ReservationBadge';
import { getDateLocale } from '../../lib/base-i18n';
import { railTimeLabel } from './lib/railTimeLabel';
import { TypeIcon } from './calendarTypeMeta';
import { useMyUserId, isMineEvent } from './lib/eventOwnership';

interface CalendarDayRailProps {
  /** 선택일. */
  date: Date;
  /** 그날에 걸친 일정(멀티데이 포함) — 부모가 eventsOnDay 로 계산해 넘긴다. */
  events: CalendarEventDto[];
  onEventClick: (e: CalendarEventDto) => void;
  /** '이 날짜에 일정 추가' — 기존 DayPopover(유형별 생성 메뉴)를 연다. */
  onCreate: (date: Date) => void;
  /**
   * ⭐ 카드 우상단 «캘린더 색 점» (옛 가지 a44d93dcd · b51db75c2 · 2026-10-13 이식).
   *   ⚠ 미지정이면 점을 아예 안 그린다 ⇒ 종전과 100% 같다(회귀 안전).
   */
  calendars?: CalendarDto[];
}

/**
 * 색 점을 그릴지 · 무슨 색인지 — 순수 함수(시험이 직접 잰다).
 *
 * ⭐ 팀 캘린더 일정은 «작성자와 무관하게» 점을 그린다 — 팀 색은 「누가 만들었든 이 팀 것」이다.
 * ⛔ 개인 캘린더/미지정 일정은 «내 것»일 때만 — 남의 개인 일정(참석자·팀원으로 보이는 것)에 내 색을
 *   칠하면 내 캘린더에 속한 것처럼 보인다(b51db75c2 「다른 사람 일정에는 스와치를 없애야지」).
 *   게다가 남의 개인 캘린더 색은 «알 수도 없다» — `calendars` 는 내 것(＋내 팀 것)만 담는다.
 * ⚠ 내 id 를 모르면(null) 개인 일정에는 안 그린다 — 「내 것」이라 단정할 수 없다.
 * ⚠ main 에는 `orgTeamId`/`orgTeamName` 이 없다 — 팀 판정·이름은 `teamId`/`teamName` 으로 한다.
 */
export function railSwatchOf(
  e: CalendarEventDto,
  calendarById: ReadonlyMap<string, CalendarDto>,
  myUserId: string | null,
): { color: string; title: string } | null {
  const cal = e.calendarId ? calendarById.get(e.calendarId) ?? null : null;
  const isTeamCal = !!(cal && cal.teamId);
  const isMine = myUserId != null && e.creatorId === myUserId;
  if (!isTeamCal && !isMine) return null;
  return {
    // 「기본 캘린더」(calendarId 없음)는 사이드바 점(`.calendar-cals-dot--none`, --border-strong)과 같은 회색 (2026-10-14).
    color: cal ? cal.color : 'var(--border-strong)',
    title: cal?.teamName ?? (cal ? cal.name : tCalendar('calendar.create.calendar_default')),
  };
}

/**
 * CalendarDayRail — 우측 선택일 레일 (레이아웃 참조 all-frame.html L1176~1185).
 *
 * 기존 DayPopover 는 그대로 두고(유형별 생성 메뉴가 여기엔 없다), 선택일의 일정을
 * 그리드를 가리지 않는 고정 영역에 상시 노출한다. 시간순 정렬, 종일 일정이 위.
 */
export function CalendarDayRail({ date, events, onEventClick, onCreate, calendars }: CalendarDayRailProps) {
  // 「내 일정은 굵게」 — 월간 칩·시간 블록과 «같은» 판정을 쓴다(lib/eventOwnership 한 곳).
  const myUserId = useMyUserId();
  useSyncExternalStore(subscribeCalendarLang, getCalendarLang, getCalendarLang);
  const calendarById = new Map((calendars ?? []).map((c) => [c.id, c] as const));

  // 종일 → 시작시각 순. 같은 조건이면 제목순(렌더 안정성).
  const sorted = [...events].sort((a, b) => {
    if (a.isAllDay !== b.isAllDay) return a.isAllDay ? -1 : 1;
    const d = new Date(a.startAt).getTime() - new Date(b.startAt).getTime();
    return d !== 0 ? d : a.title.localeCompare(b.title);
  });

  const heading = date.toLocaleDateString(getDateLocale(), {
    month: 'long', day: 'numeric', weekday: 'short',
  });

  return (
    <aside className="cal-day-rail" aria-label={tCalendar('calendar.shell.rail_label')}>
      <div className="cal-day-rail-head">
        <div className="cal-day-rail-date">{heading}</div>
        <div className="cal-day-rail-sub">
          {sorted.length > 0
            ? tCalendar('calendar.shell.day_count', { n: sorted.length })
            : tCalendar('calendar.shell.day_none')}
        </div>
      </div>

      <div className="cal-day-rail-list">
        {sorted.length === 0 ? (
          <div className="cal-day-rail-empty">
            <CalendarIcon size={22} aria-hidden />
            <span>{tCalendar('calendar.shell.day_empty')}</span>
          </div>
        ) : (
          sorted.map((e) => {
            const meta = TYPE_COLOR_MAP[e.type as CalendarEventType];
            const color = meta?.color ?? 'var(--text-muted)';
            const isDoneTodo = e.type === 'todo' && e.done;
            const swatch = calendars ? railSwatchOf(e, calendarById, myUserId) : null;
            return (
              <button
                key={`${e.id}-${e.occurrenceAt ?? ''}`}
                type="button"
                className={`cal-day-rail-card${isDoneTodo ? ' cal-day-rail-card--done' : ''}${isMineEvent(e, myUserId) ? ' cal-day-rail-card--mine' : ''}`}
                onClick={() => onEventClick(e)}
              >
                {/*
                  ⛔ 왼쪽 색 띠를 지웠다 (2026-09-09 사용자 결정 · 2026-09-28 반영).
                    유형은 아래 아이콘이 이미 말한다 — 띠는 같은 말을 한 번 더 한다.
                */}
                {swatch && (
                  <span className="cal-day-rail-team-swatch" style={{ background: swatch.color }} title={swatch.title} aria-hidden />
                )}
                <span className="cal-day-rail-body">
                  {/* ⭐ 2026-10-20 — 여러 날 일정이면 «월·일»도 붙인다(lib/railTimeLabel.ts). 하루 일정은 종전 글자 그대로. */}
                  {/*   좁은 카드에서 「오 / 후」처럼 낱말 가운데서 꺾이지 않게, 시작·끝 덩어리만 한 줄로 묶는다(꺾임은 « – » 에서만). */}
                  <span className="cal-day-rail-time">
                    {railTimeLabel(e.startAt, e.endAt, !!e.isAllDay).split(' – ').map((part, i) => (
                      <Fragment key={i}>
                        {i > 0 && ' – '}
                        <span className="cal-day-rail-time-part">{part}</span>
                      </Fragment>
                    ))}
                  </span>
                  <span className="cal-day-rail-title">
                    <span className="cal-day-rail-ico" style={{ color }} aria-hidden>
                      <TypeIcon type={e.type} size={12} />
                    </span>
                    <ReservationBadge event={e} variant="small" />{e.title}
                  </span>
                  {e.location && <span className="cal-day-rail-meta">{e.location}</span>}
                </span>
              </button>
            );
          })
        )}
      </div>

      <button type="button" className="cal-day-rail-add" onClick={() => onCreate(date)}>
        <Plus size={13} /> {tCalendar('calendar.shell.day_add')}
      </button>
    </aside>
  );
}
